/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {
  GenericObject
} from '@ion/core/src/types/entities';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import AddressTypeChooser from './AddressTypeChooser';
import CountryChooser from './CountryChooser';
import StateChooser from './StateChooser';

export interface AddressProps {
  onChange: (address: GenericObject) => void;
  readOnly: boolean;
  required: boolean;
  classes?: string;
  addresses: any;
  minAddress?: number;
  bottomBorder?: boolean;
  multipleAddress?: boolean;
};

const useStyles = makeStyles(theme => ({
  container : {
    margin : '0px'
  },
  typoStyle : {
    fontSize : '14px !important',
    width    : '100%'
  },
  subHeader : {
    display : 'flex',
    width   : '100%',
    padding : '16px',
    height  : '0em'
  },
  deleteStyle : {
    top : '-10px'
  }
}));

/**
 *
 * The Address Editor provides the UI which allows a user to update their default
 * Address, such as their state, country etc..
 *
 *    import {Address} from '@ion/features/src/entities/Address';
 *
 *    <Addresses
 *       onChange={onChange} />
 *
 */
export const Addresses = forwardRef(({
  readOnly,
  required,
  onChange,
  addresses,
  minAddress = 0,
  bottomBorder = true,
  multipleAddress = false
}: AddressProps, ref) => {
  const classes = useStyles();
  const [addressLevel, setAddressLevel]                  = useState(addresses || []);
  const [states, setStates]                              = useState(translate('states:us', {returnObjects: true}));
  const [focusStateField, setfocusStateField]            = useState(false);
  const [countries, setCountries]                        = useState(translate('countries:countriesList', {returnObjects: true}));

  useEffect(() => {
    if (addresses?.length > 0) {
      setAddressLevel(addresses);
    }
  }, [addresses]);

  useEffect(() => {
    if (minAddress !== 0 && isEmpty(addresses)) {
      createAddress();
    }
  }, [minAddress]);

  /**
   * create new row for Address i.e addresses
   */
  function createAddress() {
    const level = Object.assign([], addressLevel);
    const address = {
      street            : '',
      isNonUSCountry    : false,
      street2           : '',
      city              : '',
      stateProvince     : '',
      stateProvinceCd   : 'AL',
      zip               : '',
      country           : 'United States of America',
      countryCd         : 'US',
      addressType       : 'billing',
      isStreetError     : false,
      isStateError      : false,
      isCityError       : false,
      isZipError        : false,
      isZipInvalidError : false,
      isCountryError    : false
    };
    level.push({id: addressLevel.length + 1, addresses: address});
    onChange(level);
    setAddressLevel(level);
  }
  /**
   * create new row for Address i.e addresses
   */
  function deleteAddress({id}) {
    if (minAddress !== addressLevel?.length) {
      const level = addressLevel?.filter((item) => Number(item.id) !== Number(id));
      setAddressLevel(level);
      onChange(level);
    }
  }
  const onSelectCountry = function(country, {id}): void {
    const hasCountry = Boolean(country);
    const isUS       = country?.id === 'US';
    const addLevel = addressLevel.map((item) => {
      if (Number(item.id) === Number(id)) {
        item.addresses= {
          ...item.addresses,
          country         : hasCountry ? country.name : '',
          countryCd       : hasCountry ? country.id : '',
          stateProvince   : isUS ? item.addresses.stateProvince : '',
          stateProvinceCd : isUS ? item.addresses.stateProvinceCd : ''
        };
      }
      return item;
    });
    (!country || country?.id) === 'US'
      ? setStates(translate('states:us', {returnObjects: true}))
      : setStates(translate('states:noState', {returnObjects: true}));
    onChange(addLevel);
    setAddressLevel(addLevel);
    setfocusStateField(true);
    onChange(addLevel);
  };

  const onSelectStates = function(state, value): void {
    const addLevel = addressLevel.map((item) => {
      if (item.id === value.id) {
        item.addresses= {
          ...item.addresses,
          stateProvince   : state?.name || '',
          stateProvinceCd : state?.id || ''
        };
      }
      return item;
    });
    setAddressLevel(addLevel);
    onChange(addLevel);
  };

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    const {name, value, id} = e.target;
    const nameparam = name === 'stateProvince' ? name : name.toLocaleLowerCase();
    const addLevel = addressLevel.map((item) => {
      if (Number(item.id) === Number(id)) {
        item.addresses= {
          ...item.addresses,
          [nameparam] : value
        };
      }
      return item;
    });
    setAddressLevel(addLevel);
    onChange(addLevel);
  };

  /**
   * Invoked when the user selects a types.
   */
  const handleAddressTypes = function(type: string, {id}): void {
    const addLevel = addressLevel.map((item) => {
      if (Number(item.id) === Number(id)) {
        item.addresses= {
          ...item.addresses,
          addressType : type
        };
      }
      return item;
    });
    onChange(addLevel);
    setAddressLevel(addLevel);
  };

  const enableAdd = multipleAddress || (!multipleAddress && addressLevel.length === 0);

  return (
    <div className={classes.container}>
      <FormSection bottomBorder={bottomBorder}
        title={translate('contacts:address.title')}
        legend={!readOnly && enableAdd && <TertiaryButton onClick={createAddress}>{translate('contacts:address.add')}</TertiaryButton>}>
        {addressLevel?.map((item, index) => {
          return (<>
            <div className={classes.subHeader}>
              <Typography className={classes.typoStyle} component='h1' variant='h2'>
                {translate('contacts:address.subTitle', {level: index + 1})}
              </Typography>
              {!readOnly &&
              <TertiaryButton className={classes.deleteStyle} onClick={function(e) {
                deleteAddress(item);
              }}>{translate('contacts:address.delete')}</TertiaryButton>}
            </div>
            <FormItem>
              <AddressTypeChooser readOnly={readOnly}
                keyObj={item}
                defaultValue={item?.addresses?.addressType?.toLocaleLowerCase()}
                onChange={handleAddressTypes} />
            </FormItem>
            <FormItem>
              <FormTextField required
                disabled={readOnly || false}
                fieldLabel={translate('contacts:address.street')}
                placeholder={translate('contacts:address.streetPlaceHolder')}
                name={translate('contacts:address.street')}
                maxLength={64}
                id={item.id}
                onChange={handleFieldChange}
                hasError={item.addresses.isStreetError}
                errorText={item.addresses.isStreetError
                  ? translate('contacts:address.streetError') : ''}
                value={item.addresses.street} />
            </FormItem>
            <FormItem>
              <FormTextField required
                disabled={readOnly || false}
                fieldLabel={translate('contacts:address.city')}
                placeholder={translate('contacts:address.cityPlaceHolder')}
                name={translate('contacts:address.city')}
                maxLength={64}
                id={item.id}
                onChange={handleFieldChange}
                hasError={item.addresses.isCityError}
                errorText={item.addresses.isCityError
                  ? translate('contacts:address.cityError') : '' }
                value={item.addresses.city} />
            </FormItem>
            <FormItem>
              {item.addresses.countryCd === 'US' ? <StateChooser required
                key={item.id}
                states={states}
                keyObj={item}
                readOnly={readOnly}
                onChange={onSelectStates}
                focusStateField={focusStateField}
                validationError={item.addresses.isStateError
                  ? translate('contacts:address.stateError') : ''}
                defaultValue={item.addresses.stateProvince }/>
                : <FormTextField required
                  disabled={readOnly || false}
                  fieldLabel={translate('contacts:address.stateForm.label')}
                  placeholder={translate('contacts:address.stateForm.placeholderText')}
                  name={translate('contacts:address.stateForm.stateProvince')}
                  maxLength={64}
                  id={item.id}
                  onChange={handleFieldChange}
                  value={item.addresses.stateProvince}
                  hasError={item.addresses.isStateError}
                  errorText={item.addresses.isStateError
                    ? translate('contacts:address.stateTextError') : ''}
                  value={item.addresses.stateProvince} />
              }
            </FormItem>
            <FormItem>
              <FormTextField required
                disabled={readOnly || false}
                fieldLabel={translate('contacts:address.zipCode')}
                placeholder={translate('contacts:address.zipPlaceHolder')}
                name={translate('contacts:address.zip')}
                maxLength={64}
                id={item.id}
                onChange={handleFieldChange}
                hasError={item.addresses.isZipError || item.addresses.isZipInvalidError}
                errorText={item.addresses.isZipError
                  ? translate('contacts:address.zipError')
                  : (item.addresses.isZipInvalidError ? translate('contacts:address.zipInvalidError') : '')}
                value={item.addresses.zip} />
            </FormItem>
            <FormItem>
              <CountryChooser required
                key={item.id}
                keyObj={item}
                countries={countries}
                readOnly={readOnly}
                onChange={onSelectCountry}
                validationError={(item.addresses.isCountryError)
                  ? translate('contacts:address.countryError') : ''}
                defaultValue ={item.addresses.country} />
            </FormItem>
          </>
          );
        })}
      </FormSection>
    </div>
  );
});

export default Addresses;
